import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { FeedbackStepData } from '../../feedback-step.interface';
import { FeedbackDrawerParams, FeedbackQuestion } from '../../../feedback';
import {
  QuestionType,
  QUESTION_SCALE_LABELS,
  QUESTION_TYPE_MAP,
} from '../../feedback-question.enum';
import { CaseBehaviour } from '../../../../../insights.interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BehaviorGraphComponent } from './behavior-graph/behavior-graph.component';

@Component({
  selector: 'app-behavior-metrics-step',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzRadioModule,
    NzButtonModule,
    NzFormModule,
    NzDividerModule,
    NzInputModule,
    NzTabsModule,
    NzCheckboxModule,
    BehaviorGraphComponent,
  ],
  templateUrl: './behavior-metrics-step.component.html',
  styleUrls: ['../../feedback-form.component.scss'],
})
export class BehaviorMetricsStepComponent implements OnChanges, OnDestroy {
  @Input() stepData!: FeedbackStepData;
  @Input() feedbackDetails!: FeedbackDrawerParams;
  @Input() formGroup!: FormGroup;
  @Input() onNext!: () => void;
  @Input() onPrevious!: () => void;
  @Input() overAllScore = 0;
  @Input() selectedBehaviorId: number | null = null;
  @Input() notObservedBehaviorIds: number[] = [];

  @Output() behaviorTabChanged = new EventEmitter<number>();
  @Output() notObservedForBehaviorChanged = new EventEmitter<{
    behaviorId: number;
    isNotObserved: boolean;
  }>();

  caseBehvaiors: CaseBehaviour[] = [];
  private readonly destroy$ = new Subject<void>();
  private hasBehaviorSubscription = false;

  ngOnChanges(): void {
    this.setupValidation();
    if (this.feedbackDetails?.caseDetail) {
      this.caseBehvaiors =
        this.feedbackDetails.caseDetail.caseBehaviors?.filter(
          behavior => !behavior.draft && behavior.statusId !== 2
        ) || [];

      if (this.caseBehvaiors.length > 0) {
        const behaviorIdToApply =
          this.selectedBehaviorId ||
          this.formGroup.get('caseBehaviorMappingId')?.value ||
          this.caseBehvaiors[0].id;

        this.applySelectedBehavior(behaviorIdToApply);
      }

      this.setupBehaviorCategorySubscription();
    }

    if (this.stepData && this.formGroup) {
      this.setupValidation();
    }
  }

  private setupBehaviorCategorySubscription(): void {
    if (this.hasBehaviorSubscription) {
      return;
    }

    const behaviorCategoryControl = this.formGroup.get('caseBehaviorMappingId');
    if (behaviorCategoryControl) {
      this.hasBehaviorSubscription = true;
      behaviorCategoryControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((selectedBehaviorId: number) => {
          if (selectedBehaviorId) {
            this.updateBehaviorSpecificQuestions(selectedBehaviorId);
          }
        });
    }
  }

  private applySelectedBehavior(behaviorId: number): void {
    if (!behaviorId) {
      return;
    }

    this.selectedBehaviorId = behaviorId;
    this.formGroup.patchValue({
      caseBehaviorMappingId: behaviorId,
    });
    this.updateBehaviorSpecificQuestions(behaviorId);
  }

  getSelectedBehaviorTabIndex(): number {
    if (!this.caseBehvaiors.length) {
      return 0;
    }

    const selectedId =
      this.selectedBehaviorId ||
      this.formGroup.get('caseBehaviorMappingId')?.value;
    const selectedIndex = this.caseBehvaiors.findIndex(
      behavior => behavior.id === selectedId
    );
    return Math.max(selectedIndex, 0);
  }

  onBehaviorTabIndexChange(index: number): void {
    const behavior = this.caseBehvaiors[index];
    if (!behavior) {
      return;
    }

    this.applySelectedBehavior(behavior.id);
    this.behaviorTabChanged.emit(behavior.id);
  }

  get isCurrentBehaviorNotObserved(): boolean {
    const behaviorId = this.selectedBehaviorId;
    if (!behaviorId) {
      return false;
    }
    return this.notObservedBehaviorIds.includes(behaviorId);
  }

  onNotObservedChange(isNotObserved: boolean): void {
    if (!this.selectedBehaviorId) {
      return;
    }

    this.notObservedForBehaviorChanged.emit({
      behaviorId: this.selectedBehaviorId,
      isNotObserved,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupValidation() {
    this.validateBehaviorCategory();
    this.validateQuestionResponses();
  }

  private validateBehaviorCategory(): void {
    const behaviorCategoryControl = this.formGroup.get('caseBehaviorMappingId');
    if (!behaviorCategoryControl || this.caseBehvaiors.length === 0) {
      return;
    }

    if (!behaviorCategoryControl.value) {
      this.formGroup.patchValue({
        caseBehaviorMappingId: this.caseBehvaiors[0].id,
      });
    }

    const questionType =
      this.stepData.questions.length > 0
        ? this.getQuestionType(this.stepData.questions[0])
        : '';
    if (questionType !== 'text') {
      behaviorCategoryControl.setValidators([Validators.required]);
    }
    behaviorCategoryControl.updateValueAndValidity();
  }

  private validateQuestionResponses(): void {
    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;

    for (const question of this.stepData.questions) {
      this.setupQuestionValidation(question, questionResponses);
    }
  }

  private setupQuestionValidation(
    question: FeedbackQuestion,
    questionResponses: FormArray
  ): void {
    const index = this.getQuestionIndex(question.id);
    if (index < 0) {
      return;
    }

    const control = questionResponses.at(index);
    const questionType = this.getQuestionType(question);

    if (questionType === 'text') {
      this.clearTextQuestionValidation(control);
    } else {
      this.setRatingScaleValidation(control);
    }

    this.updateQuestionId(control, question.id);
  }

  private clearTextQuestionValidation(control: AbstractControl): void {
    const descriptionControl = control.get('description');
    if (descriptionControl) {
      descriptionControl.setValidators([Validators.maxLength(500)]);
      descriptionControl.updateValueAndValidity();
    }
  }

  private setRatingScaleValidation(control: AbstractControl): void {
    const ratingScaleControl = control.get('ratingScale');
    if (ratingScaleControl) {
      ratingScaleControl.setValidators([Validators.required]);
      ratingScaleControl.updateValueAndValidity();
    }
  }

  private updateQuestionId(control: AbstractControl, questionId: number): void {
    const questionIdControl = control.get('caseFeedbackQuestionId');
    if (questionIdControl && questionIdControl.value !== questionId) {
      questionIdControl.setValue(questionId);
    }
  }

  private updateBehaviorSpecificQuestions(selectedBehaviorId: number): void {
    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;

    for (const question of this.stepData.questions) {
      const index = this.getQuestionIndex(question.id);
      if (index >= 0) {
        const control = questionResponses.at(index);
        const caseBehaviorMappingId = this.isBehaviorSpecificQuestion(question)
          ? selectedBehaviorId
          : 0;

        control.patchValue({
          caseBehaviorMappingId: caseBehaviorMappingId,
        });
      }
    }
  }

  private isBehaviorSpecificQuestion(question: FeedbackQuestion): boolean {
    return question.category === 'BehaviorSpecificMetrics';
  }
  getQuestionIndex(questionId: number): number {
    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;
    return questionResponses.controls.findIndex(
      control => control.get('caseFeedbackQuestionId')?.value === questionId
    );
  }

  getQuestionType(question: FeedbackQuestion): string {
    return QUESTION_TYPE_MAP[question.id] || QuestionType.Scale;
  }

  getScaleLabel(question: FeedbackQuestion, value: number): string {
    const labels = QUESTION_SCALE_LABELS[question.id] || [
      'Very Poor',
      'Poor',
      'Fair',
      'Good',
      'Excellent',
    ];
    return labels[value - 1] || labels[0];
  }

  getBehaviorCategoryValidationStatus(): string {
    const behaviorCategoryControl = this.formGroup.get('caseBehaviorMappingId');

    if (this.caseBehvaiors.length > 0) {
      const currentValue = behaviorCategoryControl?.value;
      if (!currentValue || currentValue === 0) {
        if (
          behaviorCategoryControl?.dirty ||
          behaviorCategoryControl?.touched ||
          this.formGroup.dirty
        ) {
          return 'error';
        }
      }
    }
    return '';
  }

  getBehaviorCategoryErrorTip(): string {
    const behaviorCategoryControl = this.formGroup.get('caseBehaviorMappingId');

    if (this.caseBehvaiors.length > 0) {
      const currentValue = behaviorCategoryControl?.value;
      if (!currentValue || currentValue === 0) {
        if (
          behaviorCategoryControl?.dirty ||
          behaviorCategoryControl?.touched ||
          this.formGroup.dirty
        ) {
          return 'Please select a behavior category';
        }
      }
    }
    return '';
  }

  getQuestionValidationStatus(questionId: number): string {
    if (this.isCurrentBehaviorNotObserved) {
      return '';
    }

    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);
      const questionType = this.getQuestionType(
        this.stepData.questions.find(q => q.id === questionId)!
      );

      if (questionType === 'text') {
        const description = control.get('description');
        if (
          description?.hasError('maxlength') &&
          (description?.dirty || description?.touched)
        ) {
          return 'error';
        }
      } else {
        const ratingScale = control.get('ratingScale');
        const value = ratingScale?.value;
        const hasError = value === null || value === undefined || value === 0;
        if (
          hasError &&
          (control?.dirty || control?.touched || this.formGroup.touched)
        ) {
          return 'error';
        }
      }
    }
    return '';
  }

  getQuestionErrorTip(questionId: number): string {
    if (this.isCurrentBehaviorNotObserved) {
      return '';
    }

    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);
      const question = this.stepData.questions.find(q => q.id === questionId)!;
      const questionType = this.getQuestionType(question);

      if (questionType === 'text') {
        const description = control.get('description');
        if (
          description?.hasError('maxlength') &&
          (description?.dirty || description?.touched)
        ) {
          return `Answer cannot exceed 500 characters.`;
        }
      } else {
        const ratingScale = control.get('ratingScale');
        const value = ratingScale?.value;
        const hasError = value === null || value === undefined || value === 0;
        if (
          hasError &&
          (ratingScale?.dirty || ratingScale?.touched || this.formGroup.touched)
        ) {
          return 'Answer is required';
        }
      }
    }
    return '';
  }

  getDescriptionLength(questionId: number): number {
    const index = this.getQuestionIndex(questionId);
    if (index < 0) return 0;
    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;
    const control = questionResponses.at(index);
    return (control.get('description')?.value || '').length;
  }
}
